import { useRef, useState, useEffect, useCallback } from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

type TabType = {
    key: string;
    name: string;
    count: number;
    img?: string;
    icon?: React.ReactNode;
};

type Props = {
    tabs: TabType[];
    activeTab: string;
    onChange: (key: string) => void;
};

export default function ScrollTabs({
    tabs,
    activeTab,
    onChange,
}: Props) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);

    const checkScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;

        setCanLeft(el.scrollLeft > 0);
        setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    // Kiểm tra lại khi danh sách tab đổi (dữ liệu tải xong, tìm kiếm) hoặc khi khung đổi kích thước
    useEffect(() => {
        checkScroll();
        const el = scrollRef.current;
        if (!el || typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", checkScroll);
            return () => window.removeEventListener("resize", checkScroll);
        }
        const ro = new ResizeObserver(checkScroll);
        ro.observe(el);
        return () => ro.disconnect();
    }, [checkScroll, tabs.length]);

    const scroll = (dir: "left" | "right") => {
        const el = scrollRef.current;
        if (!el) return;

        const amount = el.clientWidth * 0.7;

        el.scrollBy({
            left: dir === "left" ? -amount : amount,
            behavior: "smooth",
        });
    };

    const arrowClass = `
        absolute top-1/2 -translate-y-1/2 z-20
        w-8 h-8 flex items-center justify-center
        rounded-full bg-white dark:bg-[#374151]
        shadow-md hover:bg-gray-100 dark:hover:bg-gray-600
        transition t-text-white border-0 cursor-pointer
    `;

    return (
        <div className="relative rounded-xl bg-white dark:bg-[#1f2937] border border-gray-200 dark:border-gray-700">

            {/* LEFT BUTTON */}
            {canLeft && (
                <button type="button" aria-label="Cuộn trái" onClick={() => scroll("left")} className={`${arrowClass} left-2`}>
                    <LeftOutlined />
                </button>
            )}

            {/* RIGHT BUTTON */}
            {canRight && (
                <button type="button" aria-label="Cuộn phải" onClick={() => scroll("right")} className={`${arrowClass} right-2`}>
                    <RightOutlined />
                </button>
            )}

            {/* TAB LIST */}
            <div
                ref={scrollRef}
                onScroll={checkScroll}
                role="tablist"
                className={`
                    flex gap-2 p-2.5
                    overflow-x-auto whitespace-nowrap
                    scrollbar-hide
                    ${canLeft ? "pl-12" : ""}
                    ${canRight ? "pr-12" : ""}
                `}
            >
                {tabs.map((tab) => {
                    const isActive = activeTab === tab.key;

                    return (
                        <button
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            key={tab.key}
                            onClick={() => onChange(tab.key)}
                            className={`
                                px-3 py-1.5 rounded-lg flex items-center gap-2 cursor-pointer border-0
                                transition-colors duration-200 text-sm font-medium flex-shrink-0
                                ${isActive
                                    ? "bg-[#3e91ab]/10 text-[#357f96] dark:bg-[#3e91ab]/25 dark:text-[#7cc4da]"
                                    : "bg-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                }
                            `}
                        >
                            {tab.img ? (
                                <img
                                    src={tab.img}
                                    alt=""
                                    className="w-[18px] h-[18px] rounded-sm object-cover"
                                />
                            ) : (
                                <span className="w-[18px] h-[18px] flex items-center justify-center text-[16px]">
                                    {tab.icon}
                                </span>
                            )}
                            {tab.name}
                            <span
                                className={`
                                    min-w-[20px] h-[18px] px-1.5 rounded-full text-[11px] leading-[18px] text-center
                                    ${isActive
                                        ? "bg-[#357f96] text-white"
                                        : "bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-white"
                                    }
                                `}
                            >
                                {tab.count}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
